import {
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { ShopImportResult } from "../../../types/shop";

interface ImportShopsModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (file: File) => Promise<ShopImportResult>;
  isLoading: boolean;
}

export const ImportShopsModal: React.FC<ImportShopsModalProps> = ({
  open,
  onClose,
  onImport,
  isLoading,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ShopImportResult | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      if (
        file.type === "text/csv" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        setFile(file);
        setError(null);
      } else {
        setError("Please upload a CSV or Excel file");
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
    maxFiles: 1,
  });

  const handleImport = async () => {
    if (!file) return;

    try {
      setError(null);
      const result = await onImport(file);
      setImportResult(result);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An error occurred during import"
      );
    }
  };

  const handleClose = () => {
    setFile(null);
    setImportResult(null);
    setError(null);
    onClose();
  };

  const handleDownloadTemplate = () => {
    // New template columns: Name (req), Address (req), Phone, Contact person, Email, Note
    const csvContent = `Name,Address,Phone,Contact person,Email,Note`;

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "shops_import_template.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Shops</DialogTitle>
      <DialogContent>
        {!importResult ? (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Upload a CSV or Excel file containing shop data. Required
                columns:
                <strong> Name</strong> and <strong>Address</strong>. Optional:
                Phone, Contact person, Email, Note.
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleDownloadTemplate}
                  sx={{ ml: 1 }}
                >
                  Download template
                </Link>
              </Typography>
            </Box>

            <Paper
              {...getRootProps()}
              sx={{
                p: 3,
                textAlign: "center",
                backgroundColor: isDragActive
                  ? "action.hover"
                  : "background.paper",
                border: "2px dashed",
                borderColor: isDragActive ? "primary.main" : "divider",
                cursor: "pointer",
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon
                sx={{ fontSize: 48, color: "action.active", mb: 2 }}
              />
              {isDragActive ? (
                <Typography>Drop the file here</Typography>
              ) : (
                <Typography>
                  Drag and drop a file here, or click to select
                </Typography>
              )}
              {file && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Selected file: {file.name}
                </Typography>
              )}
            </Paper>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Alert
              severity={importResult.errors.length > 0 ? "warning" : "success"}
              sx={{ mb: 2 }}
            >
              Successfully imported {importResult.successCount} shops
              {importResult.errors.length > 0 &&
                ` with ${importResult.errors.length} errors`}
            </Alert>

            {importResult.errors.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Errors:
                </Typography>
                <List dense>
                  {importResult.errors.map((error, index) => (
                    <ListItem key={index}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <ErrorIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={error} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {importResult.importedShops.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Imported Shops:
                </Typography>
                <List dense>
                  {importResult.importedShops.map((shop) => (
                    <ListItem key={shop.id}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={shop.name}
                        secondary={shop.address}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!importResult ? (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleImport}
              variant="contained"
              disabled={!file || isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
            >
              {isLoading ? "Importing..." : "Import"}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
